import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  Paper,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  LocationOn,
  Store as StoreIcon,
  LocalOffer,
  ArrowForward,
  Close,
  Public,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DEBOUNCE_DELAYS, PAGINATION } from "../../config/constants";
import { useAppDispatch, useAppSelector } from "../../store/store";
import {
  setSelectedMall,
  selectSelectedMallId,
} from "../../store/slices/mallsSlice";
import { useGetMallsQuery } from "../../features/malls/mallsApiSlice";
import { useSearchStoresGlobalQuery } from "../../features/stores/storesApiSlice";
import { useGetOffersByMallQuery } from "../../features/offers/offersApiSlice";
import {
  useGetEventsQuery,
  useGetEventsByMallQuery,
} from "../../features/events/eventsApiSlice";
import OfferCard from "../../components/Offers/OfferCard";
import EventCard from "../../components/Events/EventCard";
import MallSelector from "../../components/Offers/MallSelector";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";
import { gradients, boxShadows, transparentColors } from "../../utils/theme";
import {
  ExploreHeroSection,
  ExploreTabs,
  MallsSkeletonLoader,
  StoresSkeletonLoader,
  OffersSkeletonLoader,
  EventsSkeletonLoader,
} from "../../components/Explore";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { categoryConfigs as offerCategoryConfigs } from "../../utils/offerHelpers";
import { eventCategoryConfigs } from "../../utils/eventHelpers";
import type { Mall, Offer } from "../../types/offers";
import type { Event } from "../../types";

/**
 * Explore Page Component - MallInHand Premium
 *
 * Luxury shopping discovery platform for premium malls, stores, offers and events.
 * Features:
 * - Premium navy and gold theme with luxury aesthetics
 * - Unified search with smart filtering
 * - Four-tab interface with smooth transitions
 * - Rich cards with premium hover effects
 * - Infinite scroll pagination
 * - Mobile-first responsive design with glass-morphism effects
 */
const Explore: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Redux state
  const selectedMallId = useAppSelector(selectSelectedMallId);

  // Initialize activeTab from URL parameter to prevent flash on refresh
  const getInitialTab = (): "malls" | "stores" | "offers" | "events" => {
    const tabParam = searchParams.get("tab");
    if (
      tabParam === "events" ||
      tabParam === "offers" ||
      tabParam === "stores" ||
      tabParam === "malls"
    ) {
      return tabParam;
    }
    return "malls"; // Default fallback
  };

  // Local state
  const [activeTab, setActiveTab] = useState<
    "malls" | "stores" | "offers" | "events"
  >(getInitialTab());

  // Sync activeTab with URL parameter changes
  React.useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (
      tabParam === "events" ||
      tabParam === "offers" ||
      tabParam === "stores" ||
      tabParam === "malls"
    ) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [mallSelectorSearchQuery, setMallSelectorSearchQuery] = useState("");
  const [debouncedMallSelectorQuery, setDebouncedMallSelectorQuery] =
    useState("");
  const [offerCategory, setOfferCategory] = useState<string>("all");
  const [eventCategory, setEventCategory] = useState<string>("all");
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Pagination states for tabs with server-side pagination
  const [mallsPage, setMallsPage] = useState(1);
  const [offersPage, setOffersPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);

  // Accumulated data for infinite scroll
  const [allLoadedMalls, setAllLoadedMalls] = useState<Mall[]>([]);
  const [allLoadedOffers, setAllLoadedOffers] = useState<Offer[]>([]);
  const [allLoadedEvents, setAllLoadedEvents] = useState<Event[]>([]);

  // Debounce search query to optimize API calls
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, DEBOUNCE_DELAYS.SEARCH); // Wait for user to stop typing

    return () => clearTimeout(timer); // Cleanup on every searchQuery change
  }, [searchQuery]);

  // Debounce mall selector search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMallSelectorQuery(mallSelectorSearchQuery);
    }, DEBOUNCE_DELAYS.MALL_SELECTOR); // Faster response for selector

    return () => clearTimeout(timer);
  }, [mallSelectorSearchQuery]);

  // Handle scroll to slide content over hero section with bottom gap compensation
  React.useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const currentScrollY = window.scrollY;
        const maxOffset = 350;
        const offset = Math.min(currentScrollY, maxOffset);
        contentRef.current.style.transform = `translateY(-${offset}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch data with pagination
  const {
    data: mallsData,
    isLoading: mallsLoading,
    isFetching: mallsFetching,
    error: mallsError,
  } = useGetMallsQuery(
    {
      page: mallsPage,
      limit: PAGINATION.MALL_PREVIEW_LIMIT,
      searchQuery:
        activeTab === "malls" && debouncedSearchQuery
          ? debouncedSearchQuery
          : undefined,
    },
    {
      skip: activeTab !== "malls",
    },
  );

  // Fetch malls for MallSelector dropdown with search support
  // Only fetches when user types in the search box (no initial fetch)
  const shouldFetchMalls =
    (activeTab === "offers" || activeTab === "events") &&
    debouncedMallSelectorQuery &&
    debouncedMallSelectorQuery.trim().length > 0 &&
    !selectedMallId;

  const { data: allMallsData, isFetching: mallsForSelectorFetching } =
    useGetMallsQuery(
      {
        page: 1,
        limit: PAGINATION.MALL_SELECTOR_LIMIT,
        searchQuery: shouldFetchMalls ? debouncedMallSelectorQuery : "dummy",
      },
      {
        // Only fetch when user is searching (has typed something) and no mall is selected
        skip: !shouldFetchMalls,
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
      },
    );

  // Store search (only when searching) - Uses debounced query to optimize API calls
  const { data: storeSearchData, isLoading: storeSearchLoading } =
    useSearchStoresGlobalQuery(
      { searchQuery: debouncedSearchQuery },
      {
        skip:
          !debouncedSearchQuery ||
          debouncedSearchQuery.length < 3 ||
          activeTab !== "stores",
      },
    );

  // Offers data - Server-side filtering by mall and category with pagination
  // Uses /all endpoint for featured offers when no mall selected
  // Backend returns featured offers for mallId="all", specific mall offers otherwise
  const {
    data: offersData,
    isLoading: offersLoading,
    isFetching: offersFetching,
  } = useGetOffersByMallQuery(
    {
      mallId: selectedMallId || "all", // "all" = featured offers from backend
      category: offerCategory,
      sortBy: "newest",
      page: offersPage,
      limit: PAGINATION.OFFERS_PREVIEW_LIMIT,
    },
    {
      skip: activeTab !== "offers",
      // RTK Query caches by params: "all" vs specific mall = different cache entries
    },
  );

  // Events data - Use different endpoints based on mall selection
  // - No mall selected or "all": use getEvents (featured events, no pagination)
  // - Specific mall selected: use getEventsByMall (pagination support)
  const shouldUseMallEvents = Boolean(
    selectedMallId && selectedMallId !== "all",
  );

  const {
    data: featuredEventsData,
    isLoading: featuredEventsLoading,
    isFetching: featuredEventsFetching,
  } = useGetEventsQuery(
    {
      mallId: "all",
      category: eventCategory !== "all" ? eventCategory : undefined,
      status: "upcoming",
    },
    {
      skip: activeTab !== "events" || shouldUseMallEvents,
    },
  );

  const {
    data: mallEventsResponse,
    isLoading: mallEventsLoading,
    isFetching: mallEventsFetching,
  } = useGetEventsByMallQuery(
    {
      mallId: selectedMallId || "", // Provide empty string as fallback
      page: eventsPage,
      limit: PAGINATION.EVENTS_PREVIEW_LIMIT,
    },
    {
      skip: activeTab !== "events" || !shouldUseMallEvents,
    },
  );

  const eventsLoading = shouldUseMallEvents
    ? mallEventsLoading
    : featuredEventsLoading;
  const eventsFetching = shouldUseMallEvents
    ? mallEventsFetching
    : featuredEventsFetching;

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedCity(null); // Reset city filter when search changes
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Handle mall selection
  const handleMallClick = (mallId: string) => {
    dispatch(setSelectedMall(mallId));
    navigate(`/mall/${mallId}`);
  };

  // Handle store selection
  const handleStoreClick = (storeId: string, mallId?: string) => {
    if (mallId) {
      navigate(`/mall/${mallId}/store/${storeId}`);
    } else {
      navigate(`/stores/${storeId}`);
    }
  };

  // Handle offer selection
  const handleOfferClick = (offerId: string) => {
    navigate(`/offers/${offerId}`);
  };

  // Handle event selection - Events navigate via EventCard component

  // Handle tab change
  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: "malls" | "stores" | "offers" | "events",
  ) => {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue });
  };

  // Reset pagination when filters change
  useEffect(() => {
    setMallsPage(1);
    setAllLoadedMalls([]);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    setOffersPage(1);
    setAllLoadedOffers([]);
  }, [selectedMallId, offerCategory]);

  useEffect(() => {
    setEventsPage(1);
    setAllLoadedEvents([]);
  }, [selectedMallId, eventCategory, shouldUseMallEvents]);

  // Accumulate malls as pages load
  useEffect(() => {
    if (mallsData?.malls !== undefined) {
      setAllLoadedMalls((prev) => {
        if (mallsPage === 1) {
          return mallsData.malls;
        }
        // Append new malls, avoiding duplicates
        const newMalls = mallsData.malls.filter(
          (mall) => !prev.some((m) => m.id === mall.id),
        );
        return [...prev, ...newMalls];
      });
    }
  }, [mallsData, mallsPage]);

  // Accumulate offers as pages load
  useEffect(() => {
    if (offersData?.offers !== undefined) {
      setAllLoadedOffers((prev) => {
        if (offersPage === 1) {
          return offersData.offers;
        }
        // Append new offers, avoiding duplicates
        const newOffers = offersData.offers.filter(
          (offer) => !prev.some((o) => o.id === offer.id),
        );
        return [...prev, ...newOffers];
      });
    }
  }, [offersData, offersPage]);

  // Accumulate events as pages load
  useEffect(() => {
    if (shouldUseMallEvents && mallEventsResponse?.events) {
      setAllLoadedEvents((prev) => {
        if (eventsPage === 1) {
          return mallEventsResponse.events;
        }
        // Append new events, avoiding duplicates
        const newEvents = mallEventsResponse.events.filter(
          (event) => !prev.some((e) => e.id === event.id),
        );
        return [...prev, ...newEvents];
      });
    } else if (!shouldUseMallEvents && featuredEventsData) {
      // Featured events don't paginate, just set all
      setAllLoadedEvents(featuredEventsData);
    }
  }, [shouldUseMallEvents, mallEventsResponse, featuredEventsData, eventsPage]);

  // Pagination info
  const mallsHasMore =
    mallsData?.pagination &&
    mallsData.pagination.page < mallsData.pagination.totalPages;

  const offersHasMore =
    offersData?.pagination &&
    offersData.pagination.page < offersData.pagination.totalPages;

  const eventsHasMore = shouldUseMallEvents
    ? mallEventsResponse?.pagination &&
      mallEventsResponse.pagination.page <
        mallEventsResponse.pagination.totalPages
    : false; // Featured events don't have pagination

  // Load more callbacks for infinite scroll
  const loadMoreMalls = useCallback(() => {
    if (mallsHasMore && !mallsFetching) {
      setMallsPage((prev) => prev + 1);
    }
  }, [mallsHasMore, mallsFetching]);

  const loadMoreOffers = useCallback(() => {
    if (offersHasMore && !offersFetching) {
      setOffersPage((prev) => prev + 1);
    }
  }, [offersHasMore, offersFetching]);

  const loadMoreEvents = useCallback(() => {
    if (eventsHasMore && !eventsFetching) {
      setEventsPage((prev) => prev + 1);
    }
  }, [eventsHasMore, eventsFetching]);

  // Infinite scroll hooks - Only active when respective tab is active
  useInfiniteScroll(
    loadMoreMalls,
    !!mallsHasMore && activeTab === "malls",
    mallsFetching,
  );
  useInfiniteScroll(
    loadMoreOffers,
    !!offersHasMore && activeTab === "offers",
    offersFetching,
  );
  useInfiniteScroll(
    loadMoreEvents,
    !!eventsHasMore && activeTab === "events",
    eventsFetching,
  );

  // Use accumulated data directly (no client-side filtering needed)
  const malls = allLoadedMalls || []; // For Browse Malls tab (paginated/searched)
  const mallsForSelector = allMallsData?.malls || []; // For MallSelector dropdown (all malls)
  const stores = React.useMemo(
    () => storeSearchData?.stores || [],
    [storeSearchData],
  );
  const offers = allLoadedOffers || [];

  // Filter events by category (client-side since getEventsByMall doesn't support category filtering)
  const events = React.useMemo(() => {
    if (!allLoadedEvents) return [];
    if (eventCategory === "all") return allLoadedEvents;
    return allLoadedEvents.filter((event) => event.category === eventCategory);
  }, [allLoadedEvents, eventCategory]);

  // Extract unique cities from store search results
  const availableCities = React.useMemo(() => {
    if (!stores.length) return [];
    const citySet = new Set<string>();
    stores.forEach((store) => {
      if (store.city) {
        citySet.add(store.city);
      }
    });
    return Array.from(citySet).sort();
  }, [stores]);

  // Filter stores by selected city
  const filteredStores = React.useMemo(() => {
    if (!selectedCity) return stores;
    return stores.filter((store) => store.city === selectedCity);
  }, [stores, selectedCity]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: gradients.background,
        pb: 8,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Hero Section */}
      <ExploreHeroSection
        mallsCount={mallsData?.stats?.totalMalls || malls.length}
        storesCount={mallsData?.stats?.totalStores || 0}
        offersCount={mallsData?.stats?.totalOffers || 0}
        eventsCount={mallsData?.stats?.totalEvents || 0}
      />

      {/* Main Content - Slides up over hero on scroll, compensated height to prevent bottom gap */}
      <Box
        ref={contentRef}
        sx={{
          bgcolor: "white",
          py: { xs: 3, sm: 4, md: 5, lg: 6 },
          px: { xs: 0, sm: 0 },
          // Reduce bottom padding by max scroll offset to compensate for transform
          pb: "calc(max(2rem, 5vh) - 350px)",
          position: "relative",
          willChange: "transform",
          borderTopLeftRadius: { xs: 24, sm: 28, md: 32 },
          borderTopRightRadius: { xs: 24, sm: 28, md: 32 },
          boxShadow: boxShadows.deep,
          zIndex: 1100,
          mt: { xs: -3, sm: -4, md: -4 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Premium Tabs */}
          <ExploreTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            mallsCount={mallsData?.stats?.totalMalls || malls.length}
            storesCount={mallsData?.stats?.totalStores || 0}
            offersCount={mallsData?.stats?.totalOffers || 0}
            eventsCount={mallsData?.stats?.totalEvents || 0}
          />

          {/* Premium Contextual Search Bar - Only for Malls and Stores tabs */}
          {(activeTab === "malls" || activeTab === "stores") && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 4,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  background: "white",
                  boxShadow: boxShadows.card,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: boxShadows.cardHover,
                    borderColor: "primary.light",
                  },
                }}
              >
                <TextField
                  fullWidth
                  placeholder={
                    activeTab === "malls"
                      ? "Search for malls near you..."
                      : "Search for stores and brands..."
                  }
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search
                          sx={{
                            fontSize: 26,
                            color: "primary.main",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClearSearch}
                          size="small"
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              color: "error.main",
                              transform: "rotate(90deg)",
                              transition: "all 0.3s ease",
                            },
                          }}
                        >
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "background.default",
                      borderRadius: 2,
                      fontSize: "1rem",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "white",
                      },
                      "&.Mui-focused": {
                        bgcolor: "white",
                        "& .MuiInputAdornment-root svg": {
                          transform: "scale(1.1)",
                        },
                      },
                    },
                  }}
                />
              </Paper>
            </motion.div>
          )}

          {/* Tab Content */}
          <AnimatePresence>
            {activeTab === "malls" && (
              <motion.div
                key="malls"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {mallsLoading && mallsPage === 1 ? (
                  <MallsSkeletonLoader />
                ) : mallsError ? (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    Failed to load malls. Please try again later.
                  </Alert>
                ) : malls.length === 0 && !mallsFetching ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 6, md: 8 },
                      textAlign: "center",
                      bgcolor: "background.default",
                      borderRadius: 4,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Public
                      sx={{
                        fontSize: { xs: 64, md: 80 },
                        color: "primary.light",
                        mb: 3,
                        opacity: 0.7,
                      }}
                    />
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      gutterBottom
                      color="text.primary"
                    >
                      No Malls Found
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Try adjusting your search criteria or browse all available
                      malls
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                          background: gradients.navyLux,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        Shopping Malls Near You
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Discover malls with your favorite brands and amazing
                        shopping experiences
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      {malls.map((mall, index) => (
                        <Grid item xs={12} sm={6} md={4} key={mall.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            <Card
                              elevation={0}
                              sx={{
                                cursor: "pointer",
                                height: "100%",
                                transition:
                                  "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                position: "relative",
                                overflow: "hidden",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 3,
                                boxShadow: boxShadows.card,
                                "&:hover": {
                                  transform: "translateY(-8px)",
                                  boxShadow: boxShadows.cardHover,
                                  borderColor: "primary.light",
                                  "& .arrow-icon": {
                                    transform: "translateX(6px)",
                                    color: "secondary.main",
                                  },
                                  "& .mall-image": {
                                    transform: "scale(1.08)",
                                  },
                                  "& .mall-overlay": {
                                    opacity: 0.4,
                                  },
                                },
                              }}
                              onClick={() =>
                                mall.id && handleMallClick(mall.id!)
                              }
                            >
                              {/* Mall Image/Gradient */}
                              <Box
                                className="mall-image"
                                sx={{
                                  height: 200,
                                  background: mall.imageUrl
                                    ? `url(${mall.imageUrl})`
                                    : gradients.navyLux,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  position: "relative",
                                  overflow: "hidden",
                                  transition:
                                    "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                {/* Premium Overlay with Gold Accent */}
                                <Box
                                  className="mall-overlay"
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: mall.imageUrl
                                      ? `linear-gradient(135deg, rgba(26,35,126,0.2) 0%, rgba(212,175,55,0.15) 100%)`
                                      : `linear-gradient(135deg, rgba(26,35,126,0.9) 0%, rgba(57,73,171,0.9) 100%)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {!mall.imageUrl && (
                                    <Public
                                      sx={{
                                        fontSize: 72,
                                        opacity: 0.3,
                                        color: "white",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              <CardContent sx={{ p: 3 }}>
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  gutterBottom
                                >
                                  {mall.name}
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mb: 2,
                                  }}
                                >
                                  <LocationOn
                                    sx={{
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {mall.location}, {mall.city}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    mb: 2,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {(mall.activeOffers ?? 0) > 0 && (
                                    <Chip
                                      size="small"
                                      icon={<LocalOffer />}
                                      label={`${mall.activeOffers} Offer${
                                        mall.activeOffers! > 1 ? "s" : ""
                                      }`}
                                      sx={{
                                        bgcolor: "primary.main",
                                        color: "white",
                                        fontWeight: 600,
                                        "& .MuiChip-icon": {
                                          color: "white",
                                          fontSize: 16,
                                        },
                                      }}
                                    />
                                  )}
                                  {(mall.activeEvents ?? 0) > 0 && (
                                    <Chip
                                      size="small"
                                      label={`${mall.activeEvents} Event${
                                        mall.activeEvents! > 1 ? "s" : ""
                                      }`}
                                      icon={
                                        <span style={{ fontSize: 16 }}>🎉</span>
                                      }
                                      sx={{
                                        bgcolor: "success.main",
                                        color: "white",
                                        fontWeight: 600,
                                        "& .MuiChip-icon": {
                                          color: "white",
                                        },
                                      }}
                                    />
                                  )}
                                  {(mall.activeStores ?? 0) > 0 && (
                                    <Chip
                                      size="small"
                                      icon={<StoreIcon />}
                                      label={`${mall.activeStores} Store${
                                        mall.activeStores! > 1 ? "s" : ""
                                      }`}
                                      sx={{
                                        bgcolor: "secondary.main",
                                        color: "white",
                                        fontWeight: 600,
                                        "& .MuiChip-icon": {
                                          color: "white",
                                          fontSize: 16,
                                        },
                                      }}
                                    />
                                  )}
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    color: "primary.main",
                                    fontWeight: 600,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={600}>
                                    Explore Mall
                                  </Typography>
                                  <ArrowForward
                                    className="arrow-icon"
                                    sx={{
                                      fontSize: 20,
                                      transition: "transform 0.3s ease",
                                    }}
                                  />
                                </Box>
                              </CardContent>
                            </Card>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Loading indicator for infinite scroll */}
                    {mallsFetching && mallsPage > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 4,
                          mt: 2,
                        }}
                      >
                        <CircularProgress size={40} />
                        <Typography
                          variant="body2"
                          sx={{ ml: 2, color: "text.secondary" }}
                        >
                          Loading more malls...
                        </Typography>
                      </Box>
                    )}

                    {/* End of results message - Only show when not fetching */}
                    {!mallsHasMore && malls.length > 0 && !mallsFetching && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                          mt: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          You've reached the end of the malls
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {activeTab === "stores" && (
              <motion.div
                key="stores"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Search Stores
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Find stores and brands across all malls
                  </Typography>
                </Box>

                {/* Dynamic City Filters - Show only when results span multiple cities */}
                {availableCities.length > 1 &&
                  debouncedSearchQuery.length >= 3 && (
                    <Paper
                      elevation={0}
                      sx={{ p: 2.5, mb: 3, bgcolor: "grey.50" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1.5,
                        }}
                      >
                        <LocationOn
                          sx={{ fontSize: 20, color: "primary.main" }}
                        />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Filter by Location
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label="All Cities"
                          onClick={() => setSelectedCity(null)}
                          color={!selectedCity ? "primary" : "default"}
                          variant={!selectedCity ? "filled" : "outlined"}
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            fontWeight: !selectedCity ? 600 : 400,
                          }}
                        />
                        {availableCities.map((city) => (
                          <Chip
                            key={city}
                            label={city}
                            onClick={() => setSelectedCity(city)}
                            color={
                              selectedCity === city ? "primary" : "default"
                            }
                            variant={
                              selectedCity === city ? "filled" : "outlined"
                            }
                            sx={{
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              fontWeight: selectedCity === city ? 600 : 400,
                            }}
                          />
                        ))}
                      </Box>
                    </Paper>
                  )}

                {/* Store Search Results */}
                {debouncedSearchQuery.length >= 3 ? (
                  storeSearchLoading ? (
                    <StoresSkeletonLoader />
                  ) : filteredStores.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 6,
                        textAlign: "center",
                        bgcolor: "grey.50",
                        borderRadius: 3,
                      }}
                    >
                      <StoreIcon
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      />
                      <Typography variant="h6" gutterBottom>
                        No stores found, try a different term
                      </Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        {selectedCity
                          ? `No stores found for "${searchQuery}" in ${selectedCity}`
                          : `Try searching for "${searchQuery}" or try a different term`}
                      </Typography>
                      {selectedCity && (
                        <Button
                          onClick={() => setSelectedCity(null)}
                          sx={{ mt: 2 }}
                          variant="outlined"
                        >
                          Clear Location Filter
                        </Button>
                      )} */}
                    </Paper>
                  ) : (
                    <>
                      {!selectedCity && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Showing {stores.length} store
                          {stores.length !== 1 ? "s" : ""}
                        </Typography>
                      )}
                      {selectedCity && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Showing {filteredStores.length} store
                          {filteredStores.length !== 1 ? "s" : ""} in{" "}
                          {selectedCity}
                        </Typography>
                      )}
                      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                        {filteredStores.map((store, index) => (
                          <Grid item xs={12} sm={6} md={4} key={store.id}>
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.3,
                                delay: index * 0.05,
                              }}
                            >
                              <Card
                                elevation={0}
                                sx={{
                                  cursor: "pointer",
                                  height: "100%",
                                  borderRadius: { xs: 2.5, sm: 3, md: 3.5 },
                                  border: "1px solid",
                                  borderColor: "divider",
                                  boxShadow: boxShadows.card,
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                  "&:hover": {
                                    transform: "translateY(-6px)",
                                    boxShadow: boxShadows.cardHover,
                                    borderColor: "primary.light",
                                  },
                                }}
                                onClick={() =>
                                  handleStoreClick(store.id, store.mallId)
                                }
                              >
                                <CardContent
                                  sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: { xs: 1.5, sm: 2 },
                                    }}
                                  >
                                    <Avatar
                                      src={store.logoUrl || store.imageUrl}
                                      sx={{
                                        width: { xs: 48, sm: 52, md: 56 },
                                        height: { xs: 48, sm: 52, md: 56 },
                                        background: gradients.navyLux,
                                        boxShadow: boxShadows.card,
                                      }}
                                    >
                                      {!store.logoUrl && !store.imageUrl && (
                                        <StoreIcon
                                          sx={{
                                            fontSize: {
                                              xs: 24,
                                              sm: 26,
                                              md: 28,
                                            },
                                          }}
                                        />
                                      )}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                        gutterBottom
                                        sx={{
                                          fontSize: {
                                            xs: "0.938rem",
                                            sm: "1rem",
                                            md: "1.063rem",
                                          },
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          color: "text.primary",
                                        }}
                                      >
                                        {store.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          mb: 1,
                                          fontSize: {
                                            xs: "0.75rem",
                                            sm: "0.813rem",
                                          },
                                        }}
                                      >
                                        <LocationOn
                                          sx={{
                                            fontSize: {
                                              xs: 14,
                                              sm: 15,
                                              md: 16,
                                            },
                                          }}
                                        />
                                        <Box
                                          component="span"
                                          sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {store.mallName} • {store.city}
                                        </Box>
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={store.category}
                                        sx={{
                                          fontSize: {
                                            xs: "0.688rem",
                                            sm: "0.75rem",
                                          },
                                          height: { xs: 22, sm: 24 },
                                          fontWeight: 600,
                                          background: gradients.navyLux,
                                          color: "white",
                                          boxShadow: boxShadows.card,
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 6, md: 8 },
                      textAlign: "center",
                      bgcolor: "background.default",
                      borderRadius: 4,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Search
                      sx={{
                        fontSize: { xs: 60, md: 80 },
                        color: "primary.light",
                        mb: 3,
                        opacity: 0.7,
                      }}
                    />
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      gutterBottom
                      color="text.primary"
                    >
                      Start Your Search
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ maxWidth: 400, mx: "auto" }}
                    >
                      Enter a store name or brand to discover it across all
                      shopping malls
                    </Typography>
                  </Paper>
                )}
              </motion.div>
            )}

            {activeTab === "offers" && (
              <motion.div
                key="offers"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Mall Selector Section - Consistent with Search Bar */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 4,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      background: "white",
                      boxShadow: boxShadows.card,
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                          background: gradients.navyLux,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        Special Offers
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {" "}
                        Select a mall to discover amazing deals and discounts
                      </Typography>
                    </Box>
                    <MallSelector
                      malls={mallsForSelector}
                      inputValue={mallSelectorSearchQuery}
                      onInputChange={setMallSelectorSearchQuery}
                      loading={mallsForSelectorFetching}
                      disabled={mallsForSelectorFetching}
                    />
                  </Paper>
                </motion.div>

                {/* Premium Category Filters */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
                    mb: { xs: 3, sm: 4 },
                    bgcolor: "background.default",
                    borderRadius: { xs: 2.5, sm: 3, md: 3.5 },
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: boxShadows.card,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    gutterBottom
                    sx={{
                      mb: { xs: 2, sm: 2.5, md: 3 },
                      color: "primary.main",
                      letterSpacing: "0.5px",
                      fontSize: { xs: "0.938rem", sm: "1rem", md: "1.063rem" },
                    }}
                  >
                    Browse by Category
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 1.5 },
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      pb: 1,
                      // Premium scrollbar styling
                      "&::-webkit-scrollbar": {
                        height: { xs: 5, sm: 6 },
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: transparentColors.black05,
                        borderRadius: 3,
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "primary.light",
                        borderRadius: 3,
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      },
                    }}
                  >
                    {Object.entries(offerCategoryConfigs).map(
                      ([key, config]) => (
                        <Chip
                          key={key}
                          icon={<config.icon />}
                          label={config.label}
                          onClick={() => setOfferCategory(key)}
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                            bgcolor:
                              offerCategory === key
                                ? `${config.color}20`
                                : "white",
                            borderColor:
                              offerCategory === key ? config.color : "divider",
                            color:
                              offerCategory === key
                                ? config.color
                                : "text.primary",
                            fontWeight: offerCategory === key ? 700 : 500,
                            px: { xs: 1.5, sm: 2, md: 2.5 },
                            py: { xs: 2, sm: 2.5 },
                            fontSize: {
                              xs: "0.813rem",
                              sm: "0.875rem",
                              md: "0.938rem",
                            },
                            boxShadow:
                              offerCategory === key
                                ? `0 4px 12px ${config.color}30`
                                : "none",
                            "&:hover": {
                              bgcolor: `${config.color}25`,
                              borderColor: config.color,
                              transform: "translateY(-3px)",
                              boxShadow: `0 6px 16px ${config.color}40`,
                            },
                            "& .MuiChip-icon": {
                              color:
                                offerCategory === key
                                  ? config.color
                                  : "inherit",
                              fontSize: { xs: 18, sm: 20, md: 22 },
                            },
                          }}
                          variant={
                            offerCategory === key ? "filled" : "outlined"
                          }
                        />
                      ),
                    )}
                  </Box>
                </Paper>

                {/* Offers Grid */}
                {offersLoading && offersPage === 1 ? (
                  <OffersSkeletonLoader />
                ) : offers.length === 0 && !offersFetching ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 6, md: 8 },
                      textAlign: "center",
                      bgcolor: "grey.50",
                      borderRadius: 3,
                    }}
                  >
                    <LocalOffer
                      sx={{
                        fontSize: { xs: 56, md: 72 },
                        color: "text.secondary",
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {selectedMallId
                        ? "No offers available"
                        : "No Featured Offers"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedMallId
                        ? "Try selecting a different category or mall"
                        : "Select a mall from the dropdown to discover exclusive deals"}
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {offers.length}{" "}
                        {offers.length === 1 ? "Offer" : "Offers"} Available
                      </Typography>
                    </Box>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      {offers.map((offer, index) => (
                        <Grid item xs={12} sm={6} md={4} key={offer.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            <Box
                              onClick={() => handleOfferClick(offer.id)}
                              sx={{ cursor: "pointer" }}
                            >
                              <OfferCard offer={offer} />
                            </Box>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Loading indicator for infinite scroll */}
                    {offersFetching && offersPage > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 4,
                          mt: 2,
                        }}
                      >
                        <CircularProgress size={40} />
                        <Typography
                          variant="body2"
                          sx={{ ml: 2, color: "text.secondary" }}
                        >
                          Loading more offers...
                        </Typography>
                      </Box>
                    )}

                    {/* End of results message - Only show when not fetching */}
                    {!offersHasMore && offers.length > 0 && !offersFetching && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                          mt: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          You've reached the end of the offers
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {/* Events Tab */}
            {activeTab === "events" && (
              <motion.div
                key="events"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Mall Selector Section - Consistent with Search Bar */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 4,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      background: "white",
                      boxShadow: boxShadows.card,
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                          background: gradients.navyLux,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        Upcoming Events
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Discover exciting events and live experiences at
                        shopping malls near you
                      </Typography>
                    </Box>
                    <MallSelector
                      malls={mallsForSelector}
                      inputValue={mallSelectorSearchQuery}
                      onInputChange={setMallSelectorSearchQuery}
                      loading={mallsForSelectorFetching}
                      disabled={mallsForSelectorFetching}
                    />
                  </Paper>
                </motion.div>

                {/* Premium Category Filters */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
                    mb: { xs: 3, sm: 4 },
                    bgcolor: "background.default",
                    borderRadius: { xs: 2.5, sm: 3, md: 3.5 },
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: boxShadows.card,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    gutterBottom
                    sx={{
                      mb: { xs: 2, sm: 2.5, md: 3 },
                      color: "primary.main",
                      letterSpacing: "0.5px",
                      fontSize: { xs: "0.938rem", sm: "1rem", md: "1.063rem" },
                    }}
                  >
                    Browse by Category
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 1.5 },
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      pb: 1,
                      // Premium scrollbar styling
                      "&::-webkit-scrollbar": {
                        height: { xs: 5, sm: 6 },
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: transparentColors.black05,
                        borderRadius: 3,
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "primary.light",
                        borderRadius: 3,
                        "&:hover": {
                          backgroundColor: "primary.main",
                        },
                      },
                    }}
                  >
                    <Chip
                      label="All Events"
                      onClick={() => setEventCategory("all")}
                      variant={eventCategory === "all" ? "filled" : "outlined"}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        fontWeight: eventCategory === "all" ? 700 : 500,
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        px: { xs: 1.5, sm: 2, md: 2.5 },
                        py: { xs: 2, sm: 2.5 },
                        fontSize: {
                          xs: "0.813rem",
                          sm: "0.875rem",
                          md: "0.938rem",
                        },
                        bgcolor:
                          eventCategory === "all" ? "primary.main" : "white",
                        color:
                          eventCategory === "all" ? "white" : "text.primary",
                        borderColor:
                          eventCategory === "all" ? "primary.main" : "divider",
                        boxShadow:
                          eventCategory === "all" ? boxShadows.primary : "none",
                        "&:hover": {
                          bgcolor:
                            eventCategory === "all"
                              ? "primary.dark"
                              : "primary.light",
                          color: "white",
                          borderColor: "primary.main",
                          transform: "translateY(-3px)",
                          boxShadow: boxShadows.primaryHover,
                        },
                      }}
                    />
                    {Object.entries(eventCategoryConfigs).map(
                      ([key, config]) => (
                        <Chip
                          key={key}
                          icon={
                            <span style={{ fontSize: 18 }}>{config.icon}</span>
                          }
                          label={config.label}
                          onClick={() => setEventCategory(key)}
                          variant={
                            eventCategory === key ? "filled" : "outlined"
                          }
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            fontWeight: eventCategory === key ? 700 : 500,
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                            px: { xs: 1.5, sm: 2, md: 2.5 },
                            py: { xs: 2, sm: 2.5 },
                            fontSize: {
                              xs: "0.813rem",
                              sm: "0.875rem",
                              md: "0.938rem",
                            },
                            bgcolor:
                              eventCategory === key ? "transparent" : "white",
                            color:
                              eventCategory === key ? "white" : "text.primary",
                            borderColor:
                              eventCategory === key ? "transparent" : "divider",
                            boxShadow:
                              eventCategory === key
                                ? "0 4px 12px rgba(0,0,0,0.15)"
                                : "none",
                            ...(eventCategory === key && {
                              background: config.gradient,
                              "& .MuiChip-icon": {
                                color: "white !important",
                              },
                            }),
                            "&:hover": {
                              background: config.gradient,
                              color: "white",
                              borderColor: "transparent",
                              transform: "translateY(-3px)",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                              "& .MuiChip-icon": {
                                color: "white !important",
                              },
                            },
                          }}
                        />
                      ),
                    )}
                  </Box>
                </Paper>

                {/* Events Grid */}
                {eventsLoading && eventsPage === 1 ? (
                  <EventsSkeletonLoader />
                ) : events.length === 0 && !eventsFetching ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 6, md: 8 },
                      textAlign: "center",
                      bgcolor: "grey.50",
                      borderRadius: 3,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: 56, md: 72 },
                        mb: 2,
                      }}
                    >
                      🎉
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {selectedMallId
                        ? "No events available"
                        : "No Featured Events"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedMallId
                        ? "Try selecting a different category or mall"
                        : "Select a mall from the dropdown to discover upcoming events"}
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {events.length}{" "}
                        {events.length === 1 ? "Event" : "Events"} Upcoming
                      </Typography>
                    </Box>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      {events.map((event, index) => (
                        <Grid item xs={12} sm={6} md={4} key={event.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            <EventCard event={event} />
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Loading indicator for infinite scroll */}
                    {eventsFetching && eventsPage > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 4,
                          mt: 2,
                        }}
                      >
                        <CircularProgress size={40} />
                        <Typography
                          variant="body2"
                          sx={{ ml: 2, color: "text.secondary" }}
                        >
                          Loading more events...
                        </Typography>
                      </Box>
                    )}

                    {/* End of results message - Only show when not fetching */}
                    {!eventsHasMore && events.length > 0 && !eventsFetching && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                          mt: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                          You've reached the end of the events
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </Box>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={120} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default Explore;
